var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};

// src/PluginServer.ts
import { ErrorCodes, getGeneration } from "@ballpark/realtime-core";
import { Forbidden, Unauthorized } from "@hocuspocus/common";
import {
  HocuspocusProvider
} from "@hocuspocus/provider";
import {
  action,
  comparer,
  makeAutoObservable,
  reaction,
  runInAction
} from "mobx";

// src/utils.ts
var shallowEqual = (obj1, obj2) => obj1 === obj2 || Object.keys(obj1).length === Object.keys(obj2).length && Object.keys(obj1).every(
  (key) => Object.prototype.hasOwnProperty.call(obj2, key) && obj1[key] === obj2[key]
);

// src/PluginServer.ts
import {
  Forbidden as Forbidden2,
  Unauthorized as Unauthorized2,
  ResetConnection,
  ConnectionTimeout
} from "@hocuspocus/common";
var PluginServer = class {
  constructor({
    attach = true,
    url,
    name,
    document,
    enabled = true,
    token,
    parameters = {},
    options = {},
    resetDocument,
    offline
  }) {
    this._disposer = null;
    this._connected = false;
    this._connecting = false;
    /**
     * Whether or not a websocket connection is currently open, this is not the same as connected,
     * this will be true whilst a connection is attempting to be established.
     *
     * See #_closeEvent for more details.
     */
    this._open = false;
    /**
     * Store the last close event, this is only then cleared when a successful connection is made.
     *
     * There is a difference between the websocket connection being open (means a connection is being
     * attempted and possibly established) and connected (means the connection is established).
     *
     * We do not clear this field when the websocket reopens because it may not succeed in
     * establishing a connection again. Anything relying on this value would keep flickering between
     * states if we have to constantly try and reconnect so instead we show the last close event even
     * after a new websocket is opened and clear it when that successfully connects or replace it with
     * the new close reason.
     */
    this._closeEvent = null;
    this._synced = false;
    this._enabled = true;
    this._provider = null;
    this.url = url;
    this.name = name;
    this.token = token;
    this._document = document;
    this._enabled = enabled;
    this._parameters = parameters;
    this._unsyncedChanges = 0;
    this.options = options;
    this._offline = offline;
    this.resetDocument = resetDocument;
    makeAutoObservable(this);
    if (attach)
      this.attach();
  }
  /**
   * Whether we're currently connected to the server, this is not the same as synced
   * as we may disconnect and reconnect multiple times with the server
   */
  get connected() {
    return this._connected;
  }
  /**
   * Whether we're currently attempting to connect to the server
   */
  get connecting() {
    return this._connecting;
  }
  /**
   * Whether the document has EVER synced with the server, this will be `true` even if
   * we're no longer connected or have reconnected since.
   */
  get synced() {
    return this._synced;
  }
  get enabled() {
    return this._enabled;
  }
  set enabled(enabled) {
    this._enabled = enabled;
  }
  get awareness() {
    var _a, _b;
    return (_b = (_a = this._provider) == null ? void 0 : _a.awareness) != null ? _b : null;
  }
  get provider() {
    return this._provider;
  }
  get document() {
    return this._document;
  }
  set document(document) {
    if (document !== this.document) {
      this._synced = false;
      this._closeEvent = null;
      this._document = document;
    }
  }
  get parameters() {
    return this._parameters;
  }
  set parameters(parameters) {
    if (!shallowEqual(this._parameters, parameters)) {
      this._parameters = parameters;
    }
  }
  set offline(offline) {
    this._offline = offline;
  }
  get hasUnsyncedChanges() {
    return this._unsyncedChanges > 0;
  }
  get open() {
    return this._open;
  }
  get closed() {
    return !this.open;
  }
  get closeEvent() {
    return this._closeEvent;
  }
  get unauthorized() {
    var _a;
    return ((_a = this._closeEvent) == null ? void 0 : _a.code) === Unauthorized.code;
  }
  get forbidden() {
    var _a;
    return ((_a = this._closeEvent) == null ? void 0 : _a.code) === Forbidden.code;
  }
  dispose() {
    var _a, _b, _c;
    (_a = this._disposer) == null ? void 0 : _a.call(this);
    this._disposer = null;
    (_b = this.provider) == null ? void 0 : _b.configuration.websocketProvider.destroy();
    (_c = this.provider) == null ? void 0 : _c.destroy();
  }
  /**
   * Call again after calling dispose() to reattach reactions, is a noop if already attached
   */
  attach() {
    if (this._disposer)
      return;
    const connectDisposer = reaction(
      () => {
        return {
          enabled: this.enabled,
          name: this.name,
          document: this.document,
          parameters: this.parameters,
          url: this.url,
          token: this.token
        };
      },
      () => this._connect(),
      {
        fireImmediately: true,
        equals: comparer.shallow
      }
    );
    const optionsDisposer = reaction(
      () => {
        return {
          options: this.options
        };
      },
      () => {
        if (this.provider)
          this.provider.setConfiguration(this.options);
      },
      {
        // options are already applied on connect, only fire on changes
        fireImmediately: false
      }
    );
    this._disposer = () => {
      connectDisposer();
      optionsDisposer();
    };
  }
  enable() {
    this._enabled = true;
  }
  disable() {
    this._enabled = false;
  }
  _connect() {
    runInAction(() => {
      var _a, _b, _c, _d;
      (_a = this.provider) == null ? void 0 : _a.configuration.websocketProvider.destroy();
      (_b = this.provider) == null ? void 0 : _b.destroy();
      this._provider = null;
      if (!this.enabled || this.name === void 0 || this.document === void 0) {
        this._open = false;
        this._connecting = false;
        this._connected = false;
        return;
      }
      this._open = false;
      this._connecting = true;
      this._connected = false;
      const { document } = this;
      const url = this.url.replace(/\/$/, "");
      const provider = new HocuspocusProvider(__spreadValues({
        // since multiplexing in HocusPocus 2.x.x the name is no longer appended to the URL and is
        // sent via websocket messages however realtime-cluster requires to know the document before
        // the websocket connection is established so it can establish the connection on the right
        // place
        url: `${url}/${this.name}`,
        name: this.name,
        document,
        parameters: __spreadValues({
          generation: getGeneration(document) || ((_d = (_c = this._offline) == null ? void 0 : _c.generation) != null ? _d : 0)
        }, this.parameters),
        token: this.token
      }, this.options));
      provider.on(
        "unsyncedChanges",
        action(() => {
          this._unsyncedChanges = provider.unsyncedChanges;
        })
      );
      provider.on(
        "synced",
        action(() => {
          var _a2;
          this._synced = true;
          (_a2 = this._offline) == null ? void 0 : _a2.updateStoredGeneration();
        })
      );
      provider.on(
        "connect",
        action(() => {
          this._connecting = false;
          this._connected = true;
          this._closeEvent = null;
        })
      );
      provider.on(
        "open",
        action(() => {
          this._open = true;
        })
      );
      provider.on(
        "close",
        action(({ event }) => {
          var _a2;
          this._open = false;
          this._closeEvent = event;
          this._connecting = false;
          this._connected = false;
          if (event.code === ErrorCodes.DOCUMENT_EXPIRED) {
            (_a2 = this._offline) == null ? void 0 : _a2.clearUpdates();
            if (this.resetDocument) {
              this._synced = false;
              this.resetDocument();
            } else {
              throw new Error("Document expired, no resetDocument function");
            }
          }
        })
      );
      this._provider = provider;
    });
  }
};

// src/react.tsx
import { usePropReaction } from "@ballpark/core";
import { useOffline } from "@ballpark/realtime-plugin-offline";
import {
  createContext,
  useContext,
  useLayoutEffect,
  useState
} from "react";
import { jsx } from "react/jsx-runtime";
var PluginServerContext = createContext(null);
function useServer() {
  const state = useContext(PluginServerContext);
  if (state === null)
    throw new Error("useServer() called outside of ServerProvider");
  return state;
}
function ServerProvider({
  children,
  options = {},
  url,
  document,
  name,
  token,
  enabled,
  parameters,
  resetDocument
}) {
  var _a;
  const offline = (_a = useOffline(false)) != null ? _a : void 0;
  const [pluginServer] = useState(
    () => new PluginServer({
      // don't attach because side effects in useState doesn't work with SSR or React StrictMode
      attach: false,
      url,
      document,
      name,
      token,
      enabled,
      parameters,
      options,
      resetDocument,
      offline
    })
  );
  useLayoutEffect(() => {
    pluginServer.attach();
    return () => {
      pluginServer.dispose();
    };
  }, [pluginServer]);
  usePropReaction(
    (props) => {
      Object.assign(pluginServer, props);
    },
    {
      url,
      document,
      name,
      token,
      enabled,
      parameters,
      options,
      resetDocument,
      offline
    }
  );
  return /* @__PURE__ */ jsx(PluginServerContext.Provider, { value: pluginServer, children });
}
export {
  ConnectionTimeout,
  Forbidden2 as Forbidden,
  PluginServer,
  ResetConnection,
  ServerProvider,
  Unauthorized2 as Unauthorized,
  useServer
};
